import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Lock, Eye, EyeOff } from 'lucide-react';
import { api } from '../config/api';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', password: '', confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth.php?action=register', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password
      });

      if (response.data.success) {
        // Auto-login after registration
        localStorage.setItem('user', JSON.stringify(response.data.user));
        navigate('/');
      } else {
        setError(response.data.message || 'Registration failed');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center" style={{ backgroundColor: '#faf8f5' }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="card border-0" style={{ backgroundColor: '#ffffff', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <div className="card-body p-5">
                <div className="text-center mb-5">
                  <div className="mb-3">
                    <span style={{ fontSize: '2.5rem', color: '#D4AF37' }}>✦</span>
                  </div>
                  <h1 className="fw-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', fontSize: '2rem', fontWeight: 600 }}>
                    Create Account
                  </h1>
                  <p className="text-muted">Join us for an exceptional dining experience</p>
                </div>

                {error && (
                  <div style={{
                    backgroundColor: '#FFEBEE', color: '#C62828', padding: '12px 16px',
                    borderRadius: '10px', fontSize: '0.85rem', marginBottom: '20px', textAlign: 'center'
                  }}>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: 500, fontSize: '0.9rem' }}>Full Name</label>
                    <div style={{ position: 'relative' }}>
                      <User size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                      <input type="text" className="form-control" name="name"
                        value={formData.name} onChange={handleChange} required
                        placeholder="John Doe" style={{ padding: '12px 16px 12px 42px' }} />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: 500, fontSize: '0.9rem' }}>Email Address</label>
                    <div style={{ position: 'relative' }}>
                      <Mail size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                      <input type="email" className="form-control" name="email"
                        value={formData.email} onChange={handleChange} required
                        placeholder="you@example.com" style={{ padding: '12px 16px 12px 42px' }} />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: 500, fontSize: '0.9rem' }}>Phone Number</label>
                    <div style={{ position: 'relative' }}>
                      <Phone size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                      <input type="tel" className="form-control" name="phone"
                        value={formData.phone} onChange={handleChange} required
                        placeholder="+256 772 206129" style={{ padding: '12px 16px 12px 42px' }} />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: 500, fontSize: '0.9rem' }}>Password</label>
                    <div style={{ position: 'relative' }}>
                      <Lock size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                      <input type={showPassword ? 'text' : 'password'} className="form-control" name="password"
                        value={formData.password} onChange={handleChange} required
                        placeholder="Min 6 characters" style={{ padding: '12px 42px 12px 42px' }} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: 0 }}>
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="form-label" style={{ fontWeight: 500, fontSize: '0.9rem' }}>Confirm Password</label>
                    <div style={{ position: 'relative' }}>
                      <Lock size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                      <input type={showPassword ? 'text' : 'password'} className="form-control" name="confirmPassword"
                        value={formData.confirmPassword} onChange={handleChange} required
                        placeholder="Re-enter password" style={{ padding: '12px 16px 12px 42px' }} />
                    </div>
                  </div>

                  <button type="submit" disabled={loading}
                    className="btn text-white w-100 mb-4"
                    style={{
                      backgroundColor: loading ? '#ccc' : '#D4AF37',
                      border: 'none', padding: '14px 32px',
                      fontSize: '1rem', letterSpacing: '1px',
                      textTransform: 'uppercase', fontWeight: 600,
                      borderRadius: '2px', boxShadow: '0 4px 20px rgba(212, 175, 55, 0.3)',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}>
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </button>

                  <p className="text-center mb-0">
                    Already have an account? <Link to="/login" className="fw-bold" style={{ color: '#D4AF37', textDecoration: 'none' }}>Sign in here</Link>
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
